import type { ChangeEvent } from 'react';
import * as React from 'react';
import { useEffect, useRef, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Icon, SearchInput, tokens } from '@hotjar/hotsauce';
import type { Site } from '@hotjar/site';
import type {
  Segment,
  SegmentWithUnsupportedFeature,
} from '@hotjar/webapp/features/Recordings/types';
import { SegmentEditCreate } from '@hotjar/webapp/features/Segments/SegmentEditCreate';
import { SegmentDropdownMenu } from '@hotjar/recordings/src/features/RecordingsList';

import { useNavigation } from '@hotjar/navigation';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { useFeatureFlag } from '@hotjar/feature-flags';
import { useAccountTrialInfo } from '@hotjar/account';
import { isSiteOnScaleOrScaleTrial } from '@hotjar/recordings/src/utils/isSiteOnScaleOrScaleTrial';
import { isOnActiveOrEligibleTrial } from '@hotjar/subscription-management';
import { ERRORS_SEGMENT_ID } from '@hotjar/webapp/domains/segments/constant';
import { SegmentItem } from './SegmentItem';
import { NoMatches } from './NoMatches';
import { Footer } from './Footer';
import { Title } from './Title';

const getSegmentDomIdAttr = (id: number) => {
  return `segmentItem-${id}`;
};

const getSegmentDomIdSelector = (id: number) => {
  return `#segmentItem-${id}`;
};

const HorizontalSplit = styled.div`
  margin: ${tokens.spacer.x2};
  border-bottom: 1px solid ${tokens.color.ui_border_light};
`;

const Container = styled.div<{ topNavHeight: number }>`
  background: ${tokens.color.ui_background};
  border-right: 1px solid ${tokens.color.neutral_200};
  height: calc(100vh + 2px - ${({ topNavHeight }) => topNavHeight}px);
  overflow-x: hidden;
  overflow-y: auto;
  padding: ${tokens.spacer.x3} 0;
  position: fixed;
  scroll-behavior: smooth;
  width: 230px;
  z-index: 1;
`;

const ItemsContainer = styled.div`
  margin-top: ${tokens.spacer.x1};
`;

const SearchContainer = styled.div`
  padding: ${tokens.spacer.x1} ${tokens.spacer.x2};
`;

export interface Props {
  segments: Array<SegmentWithUnsupportedFeature>;
  currentSegmentId: number;
  site: Site;
  onFilterClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onDelete: (segment: Segment) => void;
  onUpsellRequest: (featureName: string) => void;
  // FIME: Start using these props or remove them.
  /* eslint-disable react/no-unused-prop-types */
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
  /* eslint-enable react/no-unused-prop-types */
  onSegmentChange: (segment: Segment) => void;
}

const ToggleButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  height: 100%;
`;

export const SegmentSidebar = ({
  segments = [],
  currentSegmentId,
  site,
  onFilterClick,
  onDelete,
  onUpsellRequest,
  onSegmentChange,
}: Props): JSX.Element | null => {
  const { t } = useTranslation();

  const [isSearchShown, showSearch] = useState(false);
  const [query, setQuery] = useState('');
  const [editedSegment, setEditedSegment] = useState<Segment | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const itemsContainerRef = useRef<HTMLDivElement>(null);
  const { setIsSidebarMinimized, topBarHeight: topNavHeight } = useNavigation();
  /**
   * TODO: Refactor logic in RECS-1380 to be self-contained in hook
   */
  const isErrorCapturingFFEnabled = useFeatureFlag('error_reporting');
  const isNewScaleEnabled = useFeatureFlag('billing_new_scale');
  const isSiteInScaleTrialCohort =
    useFeatureFlag('scale_trial_cohort') && site?.effectivePlan === 'business';
  const trialInfo = useAccountTrialInfo();
  const isScaleTrial =
    (isNewScaleEnabled && isSiteInScaleTrialCohort) ||
    (!isNewScaleEnabled &&
      !isOnActiveOrEligibleTrial(trialInfo) &&
      site?.effectivePlan === 'business');

  const isScaleOrScaleTrial =
    site &&
    (trialInfo || undefined) &&
    isSiteOnScaleOrScaleTrial({ ...site, trial: { active: isOnActiveOrEligibleTrial(trialInfo) } });

  useEffect(() => {
    setIsSidebarMinimized(true);
  }, [setIsSidebarMinimized]);

  useEffect(() => {
    if (isSearchShown && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchShown]);

  useEffect(() => {
    /*
      @TODO
      Refactor to replace querySelector with manually storing a map of refs for the segment items,
      and interacting with the underlying (forwarded) ref of the final desired DOM element.
    */
    const selectedItem = itemsContainerRef?.current?.querySelector(
      getSegmentDomIdSelector(currentSegmentId),
    );

    if (selectedItem && selectedItem.getBoundingClientRect().top > window.innerHeight) {
      selectedItem.scrollIntoView();
    }
  }, [currentSegmentId]);

  const getFilteredSegments = useCallback(
    (): Array<SegmentWithUnsupportedFeature> =>
      segments.filter((segment) => {
        if (segment.id === ERRORS_SEGMENT_ID) {
          // Filtering logic for the errors segment (id: -8)

          if (!isErrorCapturingFFEnabled) {
            return false;
          }

          if (!isNewScaleEnabled && !isScaleOrScaleTrial) {
            // Hide the error segment, but only if the plan is not on business or scale and the scale trial hasn't ended yet
            return false;
          }
        }
        // apply translation only to default segments with negative index
        const segmentName = segment.id < 0 ? t(segment.name) : segment.name;
        return segmentName.toLowerCase().indexOf(query.toLowerCase()) > -1;
      }),
    [query, isErrorCapturingFFEnabled, segments, isScaleOrScaleTrial, isNewScaleEnabled],
  );

  const hasSegmentDeletePermission = usePermission('segments-delete');
  const hasSegmentUpdatePermission = usePermission('segments-update');

  if (!segments.length || !site) {
    return null;
  }

  const filteredSegments = getFilteredSegments();

  const defaultSegmentsPart = filteredSegments.filter(
    (segment) => segment.created_by_user_id === 0,
  );
  const userSegmentsPart = filteredSegments.filter((segment) => segment.created_by_user_id !== 0);
  userSegmentsPart.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Container topNavHeight={topNavHeight}>
      <div>
        {isSearchShown && (
          <SearchContainer>
            <SearchInput
              ref={searchInputRef}
              placeholder={t('common:recordings.segment-sidebar.search-segments')}
              value={query}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
              onBlur={() => !query && showSearch(false)}
              clearActionProps={{
                onClick: () => searchInputRef?.current?.focus(),
                description: 'Clear',
              }}
              size="s"
            />
          </SearchContainer>
        )}
        {!isSearchShown && <Title onSearchClick={() => showSearch(!isSearchShown)} />}
      </div>
      <ItemsContainer ref={itemsContainerRef}>
        {!filteredSegments.length && isSearchShown && <NoMatches value={query} />}
        {defaultSegmentsPart.map((segment) => (
          <SegmentItem
            domId={getSegmentDomIdAttr(segment.id)}
            site={site}
            key={segment.id}
            isSelected={currentSegmentId === segment.id}
            segment={segment}
            onUpsellRequest={onUpsellRequest}
            // Scale trial only for the errors segment
            hasScaleTrialBadge={segment.id === ERRORS_SEGMENT_ID && isScaleTrial}
          />
        ))}
        {defaultSegmentsPart.length && filteredSegments.length ? <HorizontalSplit /> : null}
        {userSegmentsPart.map((segment) => {
          const renderButton = () => (
            <SegmentDropdownMenu
              kind="ghost"
              segment={segment}
              onDeleteSegment={hasSegmentDeletePermission ? onDelete : undefined}
              onEditSegment={hasSegmentUpdatePermission ? setEditedSegment : undefined}
              onEditSlackForwarding={hasSegmentUpdatePermission ? setEditedSegment : undefined}
              trigger={
                <ToggleButton type="button">
                  <Icon icon="menu_kebab" size="m" />
                </ToggleButton>
              }
            />
          );

          return (
            <SegmentItem
              domId={getSegmentDomIdAttr(segment.id)}
              site={site}
              key={segment.id}
              isSelected={currentSegmentId === segment.id}
              segment={segment}
              renderButton={renderButton}
              onUpsellRequest={onUpsellRequest}
            />
          );
        })}
        {editedSegment && (
          <SegmentEditCreate
            isOpen
            onClose={() => setEditedSegment(null)}
            onSegmentChange={onSegmentChange}
            segment={editedSegment}
            currentSite={site}
            onUpsellRequest={onUpsellRequest}
            allowModeSelection={false}
          />
        )}
        {!query && <Footer onFilterClick={onFilterClick} />}
      </ItemsContainer>
    </Container>
  );
};
